<template>
  <v-container fluid>
    <v-row class="mt-5 d-flex align-center mb-3">
      <v-col cols="12" class="pa-2">
        <v-row class="align-center">
          <!-- System Notification Type Filter -->
          <v-col class="d-flex align-center" cols="auto">
            <ExportLinks export-key="company-user-notifications" :query="exportQuery" />

            <v-menu
              v-model="menu.type"
              max-height="300"
              offset-y
              :close-on-content-click="false"
              nudge-bottom="2"
            >
              <template v-slot:activator="{ on }">
                <v-btn color="secondary" class="text-capitalize" text v-on="on">
                  {{ translations.system_notification_type_filter[lang] }}
                  <v-icon v-text="menu.type ? icons.mdiChevronUp : icons.mdiChevronDown"></v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item
                  v-for="type in systemNotificationsSelect"
                  :key="type.value"
                  class="pl-3"
                  :class="{
                    'b-list-active': filtersData.type.includes(type.value),
                  }"
                  @click="toggleType(type.value)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon
                      :color="filtersData.type.includes(type.value) ? 'secondary' : ''"
                      v-text="
                        filtersData.type.includes(type.value)
                          ? icons.mdiCheckboxMarked
                          : icons.mdiCheckboxBlankOutline
                      "
                    ></v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="type.text"></v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Notification Category Filter -->

            <v-menu
              v-model="menu.category"
              max-height="300"
              offset-y
              :close-on-content-click="false"
              nudge-bottom="2"
            >
              <template v-slot:activator="{ on }">
                <v-btn color="secondary" class="text-capitalize" text v-on="on">
                  {{ translations.notification_category_system_filter[lang] }}
                  <v-icon
                    v-text="menu.category ? icons.mdiChevronUp : icons.mdiChevronDown"
                  ></v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item
                  v-for="category in notificationCategoriesSelect"
                  :key="category.value"
                  class="pl-3"
                  :class="{
                    'b-list-active': filtersData.category.includes(category.value),
                  }"
                  @click="toggleCategory(category.value)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon
                      :color="filtersData.category.includes(category.value) ? 'secondary' : ''"
                      v-text="
                        filtersData.category.includes(category.value)
                          ? icons.mdiCheckboxMarked
                          : icons.mdiCheckboxBlankOutline
                      "
                    ></v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="category.text"></v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-menu v-model="menu.date" offset-y :close-on-content-click="false" nudge-bottom="2">
              <template v-slot:activator="{ on }">
                <v-btn color="secondary" class="text-capitalize" text v-on="on">
                  {{ translations.date_filter[lang] }}
                  <v-icon v-text="menu.date ? icons.mdiChevronUp : icons.mdiChevronDown"></v-icon>
                </v-btn>
              </template>

              <v-card>
                <v-card-text>
                  <!-- From Date Picker with clear button -->
                  <v-menu
                    ref="fromDatePickerMenu"
                    v-model="menu.fromDatePicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-on="on"
                        v-model="filtersData.date[0]"
                        :label="translations.from_date[lang]"
                        prepend-icon="mdi-calendar"
                        readonly
                        clearable
                        @click:clear="clearFromDate"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filtersData.date[0]"
                      :max="filtersData.date[1]"
                      :locale="lang"
                    ></v-date-picker>
                  </v-menu>

                  <!-- To Date Picker with clear button -->
                  <v-menu
                    ref="toDatePickerMenu"
                    v-model="menu.toDatePicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-on="on"
                        v-model="filtersData.date[1]"
                        :label="translations.to_date[lang]"
                        prepend-icon="mdi-calendar"
                        readonly
                        clearable
                        @click:clear="clearToDate"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filtersData.date[1]"
                      :min="filtersData.date[0]"
                      :locale="lang"
                    ></v-date-picker>
                  </v-menu>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-col>
          <v-spacer></v-spacer>
          <v-col class="d-flex justify-end" cols="auto">
            <v-btn
              color="secondary"
              dark
              class="text-capitalize mt-2 mr-2"
              @click="resetOtpDialog = true"
            >
              {{ translations.resetOtpAttempts[lang] }}
            </v-btn>
          </v-col>
          <!-- Search Field -->
          <v-col class="d-flex justify-end" cols="auto">
            <b-search-field
              v-model="filtersData.q"
              class="mt-2"
              @keyup.enter="fetchNotifications"
            ></b-search-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Display selected types as chips -->
    <v-row v-if="filtersData.type.length > 0" no-gutters class="px-2 py-2">
      <v-col cols="12">
        <v-chip
          v-for="type in filtersData.type"
          :key="type"
          color="secondary"
          class="ml-2 mb-1 font-weight-medium"
          close
          @click:close="removeType(type)"
        >
          {{ getSystemNotificationNameFromType(type) }}
        </v-chip>
      </v-col>
    </v-row>

    <!-- Display selected categories as chips -->
    <v-row v-if="filtersData.category.length > 0" no-gutters class="px-2 py-2">
      <v-col cols="12">
        <v-chip
          v-for="category in filtersData.category"
          :key="category"
          color="yellow darken-3"
          class="ml-2 mb-1 font-weight-medium"
          close
          @click:close="removeCategory(category)"
        >
          {{ getNotificationCategoryNameFromSystem(category) }}
        </v-chip>
      </v-col>
    </v-row>

    <!-- Display selected date range as chips -->
    <v-row v-if="filtersData.date[0] || filtersData.date[1]" no-gutters class="px-2 py-2">
      <v-col cols="12">
        <v-chip
          color="primary"
          class="ml-2 mb-1 font-weight-medium"
          close
          @click:close="clearDateFilter"
        >
          {{ formatSelectedDateRange() }}
        </v-chip>
      </v-col>
    </v-row>

    <v-sheet class="mt-10">
      <NotificationsTable
        :notification-categories="notificationCategories"
        :system-notifications="systemNotificationsEnabled"
        @page-changed="handlePageChange"
      />
    </v-sheet>

    <v-dialog v-model="resetOtpDialog" max-width="500">
      <reset-otp-attempts-dialog
        :key="resetOtpDialog"
        @cancel="resetOtpDialog = false"
        @reset-complete="resetOtpDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { mapMutations, mapState, mapActions, mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import translations from '@/utils/translations/loyaltyPanel/customers';
import { mdiCheckboxBlankOutline, mdiChevronDown, mdiChevronUp, mdiCheckboxMarked } from '@mdi/js';
import NotificationsTable from '@/components/loyaltyPanel/customers/NotificationsTable.vue';
import ExportLinks from '@/components/general/ExportLinks.vue';
import ResetOtpAttemptsDialog from '@/components/loyaltyPanel/customers/ResetOtpAttemptsDialog.vue';

export default {
  name: 'NotificationsTab',
  components: { ExportLinks, NotificationsTable, ResetOtpAttemptsDialog },
  mixins: [translations],
  data() {
    return {
      page: +this.$route.query.page,
      filtersData: {
        q: +this.$route.query.q || '',
        type: [], // Expecting array
        category: [],
        date: [null, null], // We initialize with null dates
      },
      debouncedFetchNotifications: null,
      hasFetchedNotifications: false,
      menu: {
        type: false,
        category: false,
        date: false,
        fromDatePicker: false,
        toDatePicker: false,
      },
      resetOtpDialog: false,
      icons: {
        mdiChevronUp,
        mdiChevronDown,
        mdiCheckboxBlankOutline,
        mdiCheckboxMarked,
      },
    };
  },
  computed: {
    ...mapGetters(['systemNotificationsEnabled', 'notificationCategories', 'getPermission']),
    ...mapState(['loading', 'errorMessage', 'serverItemsLength', 'permissions', 'panelType']),
    ...mapState('loyaltyPanel', ['company']),
    ...mapState('loyaltyPanel/notifications', ['notifications', 'notificationsPagination']),
    filtersQuery() {
      let query = '';
      for (const [key, value] of Object.entries(this.filtersData)) {
        // Skip 'date' since it's handled separately below
        if (key === 'date') continue;

        if (Array.isArray(value) && value.length > 0) {
          query += `&${key}=${value.join(',')}`; // Join array values with comma
        } else if (value || value === false) {
          query += `&${key}=${value}`;
        }
      }

      // Handle date range query separately
      const dateQuery = this.formatDateQuery();
      if (dateQuery) {
        query += `&date=${dateQuery}`;
      }

      return query;
    },
    systemNotificationsSelect() {
      return this.systemNotificationsEnabled.length
        ? this.systemNotificationsEnabled.map((notification) => ({
            value: notification.type, // This will be the actual value in the select
            text: notification.type, // This will be the displayed label in the select
          }))
        : [];
    },
    notificationCategoriesSelect() {
      return this.notificationCategories.length
        ? this.notificationCategories.map((category) => ({
            value: category.system, // This will be the actual value in the select
            text: category.name, // This will be the displayed label in the select
          }))
        : [];
    },
    query() {
      return '?page=' + this.page + this.filtersQuery;
    },
    exportQuery() {
      let query = '';
      let isFirst = true; // Flag to track if it's the first parameter

      // Loop through each filter in filtersData
      for (const [key, value] of Object.entries(this.filtersData)) {
        // Skip 'date' as it's handled separately
        if (key === 'date') continue;

        // Handle array values (like type, category) only if they are non-empty
        if (Array.isArray(value) && value.length > 0) {
          query += `${isFirst ? '?' : '&'}${key}=${value.join(',')}`; // Join array values with comma
          isFirst = false; // Set flag to false after the first parameter
        }
        // Handle non-empty string or boolean (to include false as a valid value)
        else if (typeof value === 'string' && value.trim() !== '') {
          query += `${isFirst ? '?' : '&'}${key}=${encodeURIComponent(value)}`;
          isFirst = false; // Set flag to false after the first parameter
        }
      }

      // Handle date range separately (if both dates are non-null)
      const dateQuery = this.formatDateQuery();
      if (dateQuery) {
        query += `${isFirst ? '?' : '&'}date=${dateQuery}`;
        isFirst = false;
      }

      return query;
    },
    lang() {
      return this.$route.params.lang;
    },
  },
  watch: {
    permissions: {
      handler(val, oldVal) {
        this.checkPermissions();
      },
      deep: true,
    },
    $route(val) {
      if (!val.query.page) {
        this.$router.replace({
          query: {
            page: 1,
            ...this.$route.query,
          },
        });
      }
      this.fetchNotifications();
    },
    page(page) {
      const currentPage = this.$route.query.page;
      // Only push if page is different
      if (page !== currentPage) {
        this.$router.push({ query: { ...this.$route.query, page } });
      }
    },
    filtersData: {
      handler() {
        this.fetchNotifications(); // Fetch notifications whenever filters change
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions(['loadSystemNotificationsEnabled', 'loadNotificationCategories']),
    ...mapMutations(['setErrorMessage']),
    ...mapActions('loyaltyPanel/notifications', ['getNotifications']),

    checkPermissions() {
      if (
        this.permissions &&
        this.$route?.name === 'NotificationsTab' &&
        !this.getPermission('customers?.notifications?.read')
      ) {
        this.$router.replace(`/${this.lang}/${this.panelType}/forbidden-gateway`);
        return false;
      }
      return true;
    },
    // Toggle system notification type
    toggleType(type) {
      if (this.filtersData.type.includes(type)) {
        this.filtersData.type = this.filtersData.type.filter((t) => t !== type);
      } else {
        this.filtersData.type.push(type);
      }
    },
    // Toggle notification category
    toggleCategory(category) {
      if (this.filtersData.category.includes(category)) {
        this.filtersData.category = this.filtersData.category.filter((c) => c !== category);
      } else {
        this.filtersData.category.push(category);
      }
    },
    getSystemNotificationNameFromType(type) {
      const systemNotification = this.systemNotificationsSelect.find(
        (systemNotification) => systemNotification.value === type
      );
      return systemNotification.text || type;
    },
    getNotificationCategoryNameFromSystem(system) {
      const notificationCategory = this.notificationCategoriesSelect.find(
        (notificationCategory) => notificationCategory.value === system
      );
      return notificationCategory.text || system;
    },
    // Remove selected type
    removeType(type) {
      this.filtersData.type = this.filtersData.type.filter((t) => t !== type);
    },
    // Remove selected category
    removeCategory(category) {
      this.filtersData.category = this.filtersData.category.filter((c) => c !== category);
    },
    async fetchNotifications() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = setTimeout(async () => {
        let body = {
          query: this.query,
        };
        await this.getNotifications(body);
      }, 1000); // Adjust debounce time as needed
    },
    handlePageChange(newPage) {
      this.page = newPage; // Update the parent page state
      //this.fetchNotifications(); // Fetch notifications with the new page
    },
    // Clear From date
    clearFromDate() {
      this.filtersData.date[0] = null;
    },
    // Clear To date
    clearToDate() {
      this.filtersData.date[1] = null;
    },
    // Format the date query for backend
    formatDateQuery() {
      const from = this.filtersData.date[0] ? this.formatDate(this.filtersData.date[0]) : '';
      const to = this.filtersData.date[1] ? this.formatDate(this.filtersData.date[1]) : '';

      // If both dates are available, send "from,to"
      if (from && to) {
        return `${from},${to}`;
      }

      // If only "From" date is available, send "from" without a comma (from)
      if (from && !to) {
        return `${from}`;
      }

      // If only "To" date is available, send "to" with a leading comma prefix (,to)
      if (!from && to) {
        return `,${to}`;
      }

      // Return an empty string if no dates are selected
      return '';
    },
    // Format individual date to "DD-MM-YYYY"
    formatDate(date) {
      const d = new Date(date);
      return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
    },
    // Display selected date range
    formatSelectedDateRange() {
      const from = this.filtersData.date[0] ? this.formatDate(this.filtersData.date[0]) : null;
      const to = this.filtersData.date[1] ? this.formatDate(this.filtersData.date[1]) : null;

      // Construct the display string based on which dates are available
      if (from && to) {
        return `${this.translations.from[this.lang]}: ${from} / ${
          this.translations.to[this.lang]
        }: ${to}`;
      } else if (from) {
        return `${this.translations.from[this.lang]}: ${from}`;
      } else if (to) {
        return `${this.translations.to[this.lang]}: ${to}`;
      }

      // Return an empty string if neither date is selected
      return '';
    },
    // Clear the date filter
    clearDateFilter() {
      this.filtersData.date = [null, null];
    },
  },
  async mounted() {
    if (!this.checkPermissions()) {
      return;
    }
    this.filtersData.q = this.$route.query.q || '';
    this.filtersData.type = this.$route.query.type ? this.$route.query.type.split(',') : [];
    this.filtersData.category = this.$route.query.category
      ? this.$route.query.category.split(',')
      : [];
    await this.loadSystemNotificationsEnabled();
    await this.loadNotificationCategories();

    // Create the debounced function
    this.debouncedFetchNotifications = debounce(this.fetchNotifications, 1000);
  },
};
</script>
